import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TabPanel from 'components/consumer/TabPanel';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import JBImage from 'components/essentials/JBImage';
import useStoreDetails from 'hooks/useStoreDetails';
import IconReturns from 'components/icons/iconReturns';
import IconDiscountOutline from 'components/icons/iconDiscountOutline';
import useScreen from 'hooks/useScreen';
import IconCard from 'components/icons/iconCard';
import IconPayPal from 'components/icons/iconPayPal';
import IconAffirmLogo2 from 'components/icons/iconAffirmLogo2';
import icoCalendar from 'assets/icons/ico-close.svg';
import AppleIcon from 'assets/icons/apple.svg';
import PointOfSaleIcon from 'assets/icons/point-of-sale.svg';
import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';
import PointOfSaleOption from './components/PointOfSaleOption';
import CreditCardOption from './components/CreditCardOption';
import FinancingOption from './components/FinancingOption';
import ApplePayOption from './components/ApplePayOption';
import PaypalOption from './components/PaypalOption';
import TabIcon from './components/TabIcon';
import StepTitle from '../StepTitle';

const creditCardTab = {
  type: 'credit_card',
  label: 'Credit Card',
  id: 'pay-now',
  description: 'Credit or debit card',
  icon: <IconCard width="40" height="40" />,
  borderType: 'all',
};

const paypalTab = {
  type: 'paypal',
  label: 'PayPal',
  id: 'paypal',
  description: 'Paypal',
  icon: <IconPayPal width="100" height="40" />,
  borderType: 'all',
};

const pointOfSaleTab = {
  type: 'point_of_sale',
  label: 'Point of Sale',
  id: 'point-of-sale',
  description: 'Point of Sale',
  icon: <JBImage src={PointOfSaleIcon} height={32} width={32} />,
  borderType: 'all',
};

const applePayTab = {
  type: 'apple_pay',
  label: 'Apple Pay',
  id: 'apple-pay',
  description: 'Express checkout',
  icon: <JBImage src={AppleIcon} width={38} />,
  borderType: 'all',
};

const financingTab = {
  type: 'financing',
  label: 'Pay Monthly',
  id: 'pay-monthly',
  description: 'As low as ',
  icon: <IconAffirmLogo2 width="75" height="40" />,
  borderType: 'all',
};

const Payment = ({
  cart,
  posData,
  history,
  checkout,
  showError,
  placeOrder,
  payPalData,
  applePayData,
  isTradeUser,
  updatePayment,
  getCartIssues,
  onOrderSuccess,
  calculateTotal,
  validateCheckout,
}) => {
  const hideApplePayTab = usePostHogFeatureFlag('hideApplePayTab');
  const storeDetails = useStoreDetails();
  const [tabs, setTabs] = useState([]);
  const { isMobile } = useScreen();
  useEffect(() => {
    const tabsList = [];

    if (storeDetails?.id) {
      tabsList.push(pointOfSaleTab);
    }

    tabsList.push(creditCardTab);

    if (!isTradeUser) {
      tabsList.push(financingTab);
    }

    if (hideApplePayTab === 'control' && applePayData.status === 'true') {
      tabsList.push(applePayTab);
    }

    if (!storeDetails?.id) {
      tabsList.push(paypalTab);
    }

    setTabs(tabsList);
  }, [isTradeUser, applePayData, storeDetails?.id, hideApplePayTab]);

  const updateCreditCard = (split_payment, credit_cards) => {
    updatePayment({
      ...checkout.payment,
      split_payment,
      credit_cards,
    });
  };

  const placeOrderWithPOS = () =>
    placeOrder({
      ...checkout.payment,
      selected_type: 'point_of_sale',
    });

  const placeOrderWithProvider = useCallback(
    async payment => {
      const res = await placeOrder(payment);
      if (res?.orderId) {
        await onOrderSuccess(res.orderId, res.isNewCustomer);
      }
    },
    [onOrderSuccess, placeOrder]
  );

  const placeOrderWithCC = (split_payment, credit_cards) => {
    placeOrderWithProvider({
      ...checkout.payment,
      selected_type: 'credit_card',
      split_payment,
      credit_cards,
    });
  };

  const placeOrderWithApplePay = braintree_token => {
    placeOrderWithProvider({
      ...checkout.payment,
      selected_type: 'apple_pay',
      braintree_token,
    });
  };

  const placeOrderWithPayPal = useCallback(
    braintree_nonce => {
      placeOrderWithProvider({
        ...checkout.payment,
        selected_tab: 'paypal',
        selected_type: 'paypal',
        braintree_nonce,
      });
    },
    [checkout.payment, placeOrderWithProvider]
  );

  const updateLenderInfo = lender => {
    updatePayment({
      ...checkout.payment,
      lenderInfo: {
        ...lender,
      },
    });
  };

  const renderPOS = () => {
    const { orderSummary, placeOrderLoading } = checkout;

    return (
      <PointOfSaleOption
        cart={cart}
        posData={posData}
        checkout={checkout}
        showError={showError}
        onOrderSuccess={onOrderSuccess}
        grandTotal={orderSummary.grand_total}
        placeOrderLoading={placeOrderLoading}
        updateCreditCard={updateCreditCard}
        placeOrderWithPOS={placeOrderWithPOS}
      />
    );
  };

  const renderCC = () => {
    const { payment, orderSummary, placeOrderLoading } = checkout;

    return (
      <CreditCardOption
        payment={payment}
        checkout={checkout}
        grandTotal={orderSummary.grand_total}
        placeOrderLoading={placeOrderLoading}
        updateCreditCard={updateCreditCard}
        placeOrderWithCC={placeOrderWithCC}
      />
    );
  };

  const renderPaypal = () => {
    const { payment, orderSummary, placeOrderLoading } = checkout;

    return (
      <PaypalOption
        payment={payment}
        checkout={checkout}
        payPalData={payPalData}
        grandTotal={orderSummary.grand_total}
        placeOrderLoading={placeOrderLoading}
        placeOrderWithPayPal={placeOrderWithPayPal}
      />
    );
  };

  const renderApplePay = () => {
    const {
      email_address,
      shipping,
      orderSummary,
      placeOrderLoading,
    } = checkout;
    return (
      <ApplePayOption
        checkout={checkout}
        email={email_address}
        shipping={shipping}
        showError={showError}
        grandTotal={orderSummary.grand_total}
        placeOrderLoading={placeOrderLoading}
        placeOrder={placeOrderWithApplePay}
        braintreeClientToken={applePayData.clientToken}
        applePayInstance={applePayData.applePayInstance}
      />
    );
  };

  const renderFinancing = () => (
    <FinancingOption
      history={history}
      checkout={checkout}
      showError={showError}
      getCartIssues={getCartIssues}
      calculateTotal={calculateTotal}
      updateLenderInfo={updateLenderInfo}
      validateCheckout={validateCheckout}
      placeOrderLoading={checkout.placeOrderLoading}
    />
  );

  const onSelectTab = id => {
    const tab = tabs.find(t => t.id === id);
    if (tab) {
      updatePayment({
        ...checkout.payment,
        selected_tab: tab.id,
        selected_type: tab.type,
      });
    }
  };

  const getTabs = () => {
    const { orderSummary } = checkout;

    return tabs
      .map(tab => {
        if (tab.id === 'pay-now') {
          return {
            type: tab.type,
            label: tab.label,
            id: tab.id,
            contentId: `${tab.id}-content`,
            desc: tab.desc,
            customComponent: <TabIcon label={tab.label} icon={tab.icon} />,
            borderType: tab.borderType,
            content: renderCC(),
          };
        }
        if (tab.id === 'point-of-sale') {
          return {
            type: tab.type,
            label: tab.label,
            id: tab.id,
            contentId: `${tab.id}-content`,
            desc: tab.desc,
            customComponent: <TabIcon label={tab.label} icon={tab.icon} />,
            borderType: tab.borderType,
            content: renderPOS(),
          };
        }
        if (tab.id === 'paypal') {
          return {
            type: tab.type,
            label: tab.label,
            id: tab.id,
            contentId: `${tab.id}-content`,
            desc: tab.desc,
            customComponent: <TabIcon label={tab.label} icon={tab.icon} />,
            borderType: tab.borderType,
            content: renderPaypal(),
          };
        }
        if (tab.id === 'apple-pay') {
          return {
            type: tab.type,
            label: tab.label,
            id: tab.id,
            contentId: `${tab.id}-content`,
            desc: tab.desc,
            content: renderApplePay(),
            customComponent: <TabIcon label={tab.label} icon={tab.icon} />,
            borderType: tab.borderType,
          };
        }
        if (tab.id === 'pay-monthly') {
          const desc = `${tab.desc || ''}$${Intl.NumberFormat().format(
            (orderSummary.grand_total > 2000
              ? orderSummary.grand_total / 36
              : orderSummary.grand_total / 18
            ).toFixed(2)
          )}`;
          return {
            type: tab.type,
            label: tab.label,
            id: tab.id,
            contentId: `${tab.id}-content`,
            desc,
            customComponent: (
              <TabIcon
                label={tab.label}
                icon={tab.icon}
                desc={`As low as ${desc}/month`}
              />
            ),
            borderType: tab.borderType,
            content: renderFinancing(),
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  if (applePayData.status === 'loading') {
    return <ProgressLoader />;
  }

  const selectedTab = tabs.find(
    tab => tab.id === checkout.payment.selected_tab
  );

  const clearanceArray = cart.map(
    prod => parseInt(prod.stock_discount, 10) > 0
  );

  return (
    <>
      <div className="flex flex-row flex-wrap justify-between items-start">
        <StepTitle number="5">Payment Information</StepTitle>
      </div>

      <div className="payment-section relative" data-xc="payment-section">
        <TabPanel
          aria-label="Payment options"
          items={getTabs()}
          onSelectTab={onSelectTab}
          selectedTab={selectedTab?.id}
          variant="buttons"
        />
      </div>
      {isMobile && (
        <div className="mt-4 md:mt-16">
          {!clearanceArray.every(value => value === true) && (
            <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
              <div className="items-center flex flex-row mb-2.5">
                <IconReturns width="29" height="28" />
                <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3">
                  90 Day Returns
                </h3>
              </div>
              <div className="text-xs text-gray">
                Sit on it, break it in - if you don’t love your Joybird, send it
                back to us within 90 days of delivery, and you’ll get a refund
                of the order’s subtotal, minus a 50% restocking fee and shipping
                fees.
              </div>
            </div>
          )}

          {!clearanceArray.every(value => value === true) && (
            <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
              <div className="items-center flex flex-row mb-2.5">
                <JBImage src={icoCalendar} width={18} />
                <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3">
                  Changed your mind?
                </h3>
              </div>
              <div className="text-xs text-gray">
                If you need to cancel your initial order, you must cancel within
                24 hours in order to receive a full refund. All cancellations
                after 24 hours of placing the order and before the item&apos;s
                shipment will incur a 35% processing fee.
              </div>
            </div>
          )}

          {!clearanceArray.every(value => value === false) && (
            <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
              <div className="items-center flex flex-row mb-2.5">
                <IconDiscountOutline />
                <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3 ml-3 mb-0">
                  Final Sale
                </h3>
              </div>
              <div className="text-xs text-gray space-y-2">
                <p>
                  Cancellations: Cancellations must be made within 24 hours of
                  initial order placement.
                </p>
                <p>
                  Returns: Final sale items are ineligible for returns, except
                  when an item is damaged on arrival & documented by carrier.
                </p>
                <p>
                  Warranty: Structural damages or defects must be reported
                  within 14 days of delivery. Minor cosmetic damage is not
                  covered.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

Payment.propTypes = {
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      product_id: PropTypes.number,
    })
  ),
  history: PropTypes.object,
  showError: PropTypes.func,
  placeOrder: PropTypes.func,
  isTradeUser: PropTypes.bool,
  getCartIssues: PropTypes.func,
  updatePayment: PropTypes.func,
  onOrderSuccess: PropTypes.func,
  calculateTotal: PropTypes.func,
  validateCheckout: PropTypes.func,
  checkout: PropTypes.shape({
    email_address: PropTypes.string,
    shipping: PropTypes.shape({
      first_name: PropTypes.string,
    }),
    payment: PropTypes.shape({
      selected_tab: PropTypes.string,
      selected_type: PropTypes.string,
    }),
    orderSummary: PropTypes.shape({
      grand_total: PropTypes.number,
    }),
    placeOrderLoading: PropTypes.bool,
  }),
  applePayData: PropTypes.shape({
    status: PropTypes.string,
    clientToken: PropTypes.string,
    applePayInstance: PropTypes.object,
  }),
  payPalData: PropTypes.shape({
    status: PropTypes.string,
  }),
  posData: PropTypes.shape({
    idempotencyKey: PropTypes.string,
  }),
};

export default Payment;
